import React, { useCallback, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router'
import { PERMISSION_ACTION_EDIT, PERMISSION_ENTITY_SUPPLIER, ROUTE_SUPPLIERS } from '@klear/constants'
import { SupplierWizard } from '@klear/supplier-ui/supplier-wizard'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { returnUrlFromSearchParams } from '@klear/ui/wizard/return-url'
import { useSupplier } from '@klear/ui/hooks/use-suppliers'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import toInteger from 'lodash/toInteger'

export interface SupplierWizardPageProps {
  mode: 'create' | 'view' | 'edit'
}

export default function SupplierWizardPage({ mode }: SupplierWizardPageProps) {
  const { supplierId, traderId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  const id = supplierId ? toInteger(supplierId) : 0
  const tid = traderId ? toInteger(traderId) : 0
  const wizardTraderId = mode !== 'create' && tid > 0 ? tid : undefined
  const { supplier } = useSupplier(mode !== 'create' ? id : 0, wizardTraderId)
  useDocumentTitle(supplier?.name, { create: 'New Supplier', edit: 'Edit Supplier', view: 'Supplier' }[mode])

  const returnTo = useMemo(() => returnUrlFromSearchParams(new URLSearchParams(location.search)), [location.search])

  const canEditSupplier = !!user?.hasPermission(PERMISSION_ENTITY_SUPPLIER, PERMISSION_ACTION_EDIT)

  const listPath = `/${ROUTE_SUPPLIERS}`
  const exitToList = useCallback(() => navigate(listPath), [navigate, listPath])
  const exitAfterEdit = useCallback(() => navigate(returnTo ?? listPath), [navigate, returnTo, listPath])
  const handleBackDone = mode === 'edit' ? exitAfterEdit : exitToList

  const goToEdit = useCallback(() => navigate(`/${ROUTE_SUPPLIERS}/${id}/${PERMISSION_ACTION_EDIT}`), [navigate, id])

  return (
    <SupplierWizard
      mode={mode}
      supplierId={mode !== 'create' ? id : undefined}
      traderId={wizardTraderId}
      showTraderSelect={mode === 'create'}
      showIdentifiers
      showParentOrganization
      showCompanyLookup={mode === 'create' || mode === 'edit'}
      canEdit={mode === 'view' ? canEditSupplier : true}
      onEdit={mode === 'view' ? goToEdit : undefined}
      onBack={handleBackDone}
      onDone={handleBackDone}
    />
  )
}
